'use client';

import { ArrowDown02Icon, ArrowUp02Icon, CableIcon } from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import type {
  WebSocketEvidencePayload,
  WebSocketFrame,
  WebSocketHandshake,
} from './websocket-session-payload';

export type WebSocketTranscriptCardProps = {
  payload: WebSocketEvidencePayload;
};

function HandshakeRow({ handshake }: { handshake: WebSocketHandshake }) {
  const t = useTranslations('designSystem.artifacts.websocketSession');

  return (
    <Box
      width="100%"
      px={3}
      py={2}
      borderTopWidth="1px"
      borderTopColor="border.neutral.muted"
    >
      <HStack gap={2} alignItems="center" minW={0}>
        <Text fontSize="2xs" fontFamily="mono" color="fg.muted" flexShrink={0}>
          {handshake.id}
        </Text>
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {t(`handshake.${handshake.outcome}`)}
        </Text>
        {handshake.response && (
          <Text fontSize="xs" fontFamily="mono" color="fg.muted">
            {handshake.response.status}
          </Text>
        )}
      </HStack>
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.muted"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {`${handshake.request.method} ${handshake.request.url}`}
      </Text>
      {handshake.response?.body && (
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.default"
          whiteSpace="pre-wrap"
          wordBreak="break-all"
        >
          {handshake.response.body}
        </Text>
      )}
      {handshake.error && (
        <Text fontSize="xs" color="fg.error">
          {handshake.error}
        </Text>
      )}
    </Box>
  );
}

function FrameRow({ frame }: { frame: WebSocketFrame }) {
  const t = useTranslations('designSystem.artifacts.websocketSession');
  const isInbound = frame.direction === 'inbound';
  const DirectionIcon = isInbound ? ArrowDown02Icon : ArrowUp02Icon;

  return (
    <Box
      width="100%"
      px={3}
      py={2}
      borderTopWidth="1px"
      borderTopColor="border.neutral.muted"
    >
      <HStack gap={2} alignItems="center" minW={0}>
        <Box display="flex" alignItems="center" color="fg.muted">
          <DirectionIcon size={10} />
        </Box>
        <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
          {isInbound ? t('inbound') : t('outbound')}
        </Text>
        <Text
          fontSize="2xs"
          fontFamily="mono"
          color="fg.muted"
          truncate
          minW={0}
        >
          {frame.id}
        </Text>
        {frame.encoding === 'base64' && (
          <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
            {t('binary')}
          </Text>
        )}
        {frame.timestamp && (
          <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
            {frame.timestamp}
          </Text>
        )}
      </HStack>
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.default"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {frame.data}
      </Text>
    </Box>
  );
}

export function WebSocketTranscriptCard({
  payload,
}: WebSocketTranscriptCardProps) {
  const t = useTranslations('designSystem.artifacts.websocketSession');

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Box display="flex" alignItems="center" color="fg.muted">
            <CableIcon size={12} />
          </Box>
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            {t('title')}
          </Text>
        </HStack>
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.muted"
          truncate
          minW={0}
        >
          {payload.url}
        </Text>
      </HStack>

      <VStack gap={0} width="100%">
        {payload.handshake && <HandshakeRow handshake={payload.handshake} />}
        {payload.frames.map((frame) => (
          <FrameRow key={frame.id} frame={frame} />
        ))}
      </VStack>
    </Box>
  );
}
